import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { sentry } from '../services/sentry';
import { validatePassword } from '../utils/validation';
import { getApiBaseUrl } from '../utils/env';
import './ResetPasswordPage.css';

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [serverError, setServerError] = useState('');
  const [passwordStrength, setPasswordStrength] = useState(null);

  useEffect(() => {
    if (!token) {
      setServerError('Invalid reset link. Please request a new password reset.');
    }
  }, [token]);

  useEffect(() => {
    if (isSuccess) {
      const timer = setTimeout(() => {
        navigate('/login');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isSuccess, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Real-time password strength check
    if (name === 'newPassword') {
      const strength = validatePassword(value);
      setPasswordStrength(strength);
    }

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    if (serverError) {
      setServerError('');
    }
  };

  const validateForm = () => {
    const newErrors = {};

    const passwordValidation = validatePassword(formData.newPassword);
    if (!passwordValidation.isValid) {
      newErrors.newPassword = passwordValidation.message;
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!token) {
      setServerError('Invalid reset link. Please request a new password reset.');
      return;
    }

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setServerError('');

    try {
      const apiBaseUrl = getApiBaseUrl();
      const response = await axios.post(`${apiBaseUrl}/auth/reset-password`, {
        token: token,
        newPassword: formData.newPassword
      });

      const { success } = response.data;

      if (success) {
        setIsSuccess(true);
        
        // Log event
        sentry.captureMessage('Password reset successful');
      }
    } catch (error) {
      console.error('Reset password error:', error);

      const requestId = error.response?.headers?.['x-request-id'] || 'N/A';
      const errorMessage = error.response?.data?.error || 'Password reset failed. Please try again.';
      
      setServerError(`${errorMessage} (Request ID: ${requestId})`);

      // Log error to Sentry
      sentry.captureException(error, {
        requestId,
        statusCode: error.response?.status
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="reset-password-page">
        <div className="reset-password-container">
          <div className="reset-password-card">
            <div className="success-icon">✅</div>
            <h1>Password reset successful!</h1>
            <p className="success-message">
              Your password has been successfully reset. You can now log in with your new password.
            </p>
            <p className="redirect-text">
              Redirecting to sign in page in 3 seconds...
            </p>
            <Link to="/login" className="signin-button">
              Sign In Now
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="reset-password-page">
      <div className="reset-password-container">
        <div className="reset-password-card">
          {/* Header */}
          <div className="reset-password-header">
            <a href="/" className="logo" aria-label="CerebralMachines home">
              <img src="/CMLogo.png" alt="CerebralMachines" />
            </a>
            <h1>Set new password</h1>
            <p>Your new password must be different from previously used passwords.</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="reset-password-form">
            {serverError && (
              <div className="error-banner">
                {serverError}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="newPassword">New Password</label>
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="newPassword"
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleChange}
                  className={errors.newPassword ? 'error' : ''}
                  placeholder="Enter new password"
                  disabled={isLoading || !token}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex="-1"
                >
                  {showPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
              {errors.newPassword && <span className="error-text">{errors.newPassword}</span>}
              
              {/* Password strength indicator */}
              {formData.newPassword && passwordStrength && (
                <div className="password-strength">
                  <div className={`strength-bar strength-${passwordStrength.strength}`}>
                    <div className="strength-fill"></div>
                  </div>
                  <span className={`strength-text strength-${passwordStrength.strength}`}>
                    {passwordStrength.strength === 'weak' && 'Weak password'}
                    {passwordStrength.strength === 'medium' && 'Medium password'}
                    {passwordStrength.strength === 'strong' && 'Strong password'}
                  </span>
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm New Password</label>
              <div className="password-input-wrapper">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className={errors.confirmPassword ? 'error' : ''}
                  placeholder="Confirm new password"
                  disabled={isLoading || !token}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  tabIndex="-1"
                >
                  {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
              {errors.confirmPassword && <span className="error-text">{errors.confirmPassword}</span>}
            </div>

            <div className="password-requirements">
              <p className="requirements-title">Password must contain:</p>
              <ul>
                <li className={formData.newPassword.length >= 8 ? 'valid' : ''}>
                  At least 8 characters
                </li>
                <li className={/[A-Z]/.test(formData.newPassword) ? 'valid' : ''}>
                  At least one uppercase letter
                </li>
                <li className={/[0-9]/.test(formData.newPassword) ? 'valid' : ''}>
                  At least one number
                </li>
                <li className={/[!@#$%^&*]/.test(formData.newPassword) ? 'valid' : ''}>
                  At least one special character (!@#$%^&*)
                </li>
              </ul>
            </div>

            <button
              type="submit"
              className={`submit-button ${isLoading ? 'loading' : ''}`}
              disabled={isLoading || !token}
            >
              {isLoading ? 'Resetting...' : 'Reset Password'}
            </button>

            <Link to="/login" className="back-link">
              ← Back to Sign In
            </Link>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;



